/** Presentation helpers for Request Correspondence (#1069). */

import { dateFormatting } from '../../utils/dateFormatting';

/**
 * Short, stable ABSOLUTE timestamp for message bubbles ("Sep 9, 3:42 PM").
 *
 * The thread-list row used to share it and no longer does (Story 2.4, AC 5):
 * a ~90px fixed block eats the title's `flex: 1` at the 360pt dock minimum.
 * The list SHOWS `formatListWhen` and SPEAKS this one (the date's
 * `accessibilityLabel`); the bubbles show this. Two visible forms in one
 * panel is Story 2.4's decision D4, recorded in the story — not drift. (A
 * previous version of this comment pointed at `deferred-work.md`, where no
 * such record exists; corrected at code review, 2026-09-10.)
 *
 * ⚠️ NOTHING, NOT "Invalid Date, Invalid Date" (code review, 2026-09-11;
 * AC 10 amended in the same edit). Story 5.5's D6 made this the ONLY spoken
 * timestamp in the thread — every group's `accessibilityLabel` is built from
 * it — while `formatThreadWhen` and `formatDayLabel`, beside it, both
 * guard. A malformed `sentAt` therefore left a blank visible time and had a
 * screen reader announce the words "Invalid Date, Invalid Date", which is
 * worse than the silence a sighted reader gets. The guard matches
 * `formatListWhen`'s, whose comment makes the general case: this helper is
 * exported and will outlive its current callers. AC 10 pinned the function
 * byte-identical to protect it as the one spoken form everywhere, not to
 * preserve an output no caller wants; the empty string is what the panel's
 * other three date helpers already return.
 */
export const formatWhen = (iso: string): string => {
  const d = dateFormatting.parseInstant(iso);
  if (!d) return '';
  const datePart = dateFormatting.format(d, {
    month: 'short',
    day: 'numeric',
  });
  const timePart = dateFormatting.format(d, {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (!datePart || !timePart) return '';
  return `${datePart}, ${timePart}`;
};

const MS_PER_DAY = 86_400_000;

/** Calendar-day serial in the saved profile zone, not a 24-hour window. */
const startOfLocalDay = (d: Date): number => {
  const { year, month, day } = dateFormatting.calendarDay(d);
  return Date.UTC(year, month - 1, day);
};

/**
 * How many LOCAL calendar days `d` lies before `now`: 0 today, 1 yesterday,
 * negative for a future timestamp. `Math.round` absorbs a DST hour. ONE
 * arithmetic for the list, the divider and the bubble (2026-10-05), so the
 * three can never disagree about which day a message belongs to.
 */
const localDayDiff = (d: Date, now: Date): number =>
  Math.round((startOfLocalDay(now) - startOfLocalDay(d)) / MS_PER_DAY);

/** The clock alone, locale-formatted: `2:25 PM` in en-US, `14:25` in en-GB. */
const clockOf = (d: Date): string =>
  dateFormatting.format(d, { hour: 'numeric', minute: '2-digit' }) ?? '';

/** The day band's literal, when the band has one. English-only, see below. */
const DAY_WORDS: Readonly<Record<0 | 1, string>> = Object.freeze({
  0: 'Today',
  1: 'Yesterday',
});

/**
 * Whether two timestamps fall on the same LOCAL calendar day (Story 5.5,
 * AC 10, decision D5): the day-divider test. Calendar day, not a 24-hour
 * window, by the same local-midnight rule `formatListWhen` uses, so 23:59
 * and 00:01 are different days and a DST hour cannot merge or split two
 * days. An unparseable value is the same day as nothing.
 */
export const sameLocalDay = (aIso: string, bIso: string): boolean => {
  const a = dateFormatting.parseInstant(aIso);
  const b = dateFormatting.parseInstant(bIso);
  if (!a || !b) return false;
  return startOfLocalDay(a) === startOfLocalDay(b);
};

/**
 * The bubble group's (and the tombstone's) timestamp, in Microsoft Teams'
 * age bands, measured in the owner's Teams window and two screenshots on
 * 2026-10-05 (`spec-correspondence-dates-read-like-teams.md`): today → the
 * clock (`12:48 PM`); yesterday → `Yesterday 8:24 PM`; two to six days ago →
 * the long weekday and the clock (`Thursday 2:09 PM`); older → the locale's
 * numeric month and day and the clock (`9/1 1:33 PM`), with a two-digit year
 * once the year differs from `now`'s (`8/7/25 10:13 AM`).
 *
 * ⚠️ THIS REVERSES STORY 5.5's D6 BY OWNER RULING (2026-10-05). D6 gave the
 * group the clock alone because the divider above it said the day; Teams
 * repeats the day on every group and the owner asked for Teams. The record
 * names the reversal rather than leaving two decisions that disagree.
 *
 * `formatWhen` is untouched and remains the ONLY spoken form (the
 * timestamp's `accessibilityLabel`), so a screen reader hears exactly what it
 * heard before. Empty for an unparseable value, as every helper here is.
 * `Yesterday` is an ENGLISH-ONLY literal; see `formatDayLabel`.
 *
 * Split on the CALENDAR day (`localDayDiff`): 23:59 and 00:01 are different
 * bands. A FUTURE timestamp (clock skew) is never "this week" and falls to
 * the date, which is at least not wrong. `now` is a parameter so every band
 * is testable; callers leave it.
 */
export const formatThreadWhen = (
  iso: string,
  now: Date = new Date(),
): string => {
  const d = dateFormatting.parseInstant(iso);
  if (!d) return '';
  const dayDiff = localDayDiff(d, now);
  const clock = clockOf(d);
  if (dayDiff === 0) return clock;
  if (dayDiff === 1) return `${DAY_WORDS[1]} ${clock}`;
  if (dayDiff > 1 && dayDiff < 7) {
    return `${dateFormatting.format(d, { weekday: 'long' }) ?? ''} ${clock}`;
  }
  const date =
    dateFormatting.format(d, {
      month: 'numeric',
      day: 'numeric',
      ...(dateFormatting.calendarDay(d).year !==
      dateFormatting.calendarDay(now).year
        ? { year: '2-digit' }
        : {}),
    }) ?? '';
  return `${date} ${clock}`;
};

/**
 * The day divider's label, in Microsoft Teams' age bands, measured
 * 2026-10-05 (see `formatThreadWhen`): `Today`, `Yesterday`, the long
 * weekday (`Thursday`) for two to six days ago, then the locale's long month
 * and day (`September 1`), with the year once it differs from `now`'s
 * (`August 7, 2025`). Replaces Story 5.5 D5's `Sep 9` / `Sep 9, 2025` and
 * Story 8.7 D2's "a heading is spelled" ruling, by the owner's 2026-10-05
 * ruling that the thread follows Teams; the LIST (`formatListWhen`) keeps
 * 8.7's Outlook rule, which D1 settled and Teams' list did not contradict.
 *
 * Seven, not six, is the first day on the date branch: at seven the weekday
 * would repeat today's and say nothing, the same reason `formatListWhen`
 * gives. A future timestamp falls to the date.
 *
 * ⚠️ `Today` AND `Yesterday` ARE ENGLISH-ONLY LITERALS, BY CHOICE. Every
 * other string here comes from `Intl` with the device locale; these two do
 * not. The app is English-only today and `attachmentSummary` already made
 * the same call for its plural; on a non-English device the thread would
 * read `Yesterday 14:09` beside `Donnerstag 14:09`. `Intl.RelativeTimeFormat`
 * with `numeric: 'auto'` is the way out when a second language arrives (not
 * measured on this app's engines; code review 2026-10-05 struck an earlier
 * claim here that it was unavailable). Recorded so it is not copied as a
 * durable pattern.
 */
export const formatDayLabel = (iso: string, now: Date = new Date()): string => {
  const d = dateFormatting.parseInstant(iso);
  if (!d) return '';
  const dayDiff = localDayDiff(d, now);
  if (dayDiff === 0) return DAY_WORDS[0];
  if (dayDiff === 1) return DAY_WORDS[1];
  if (dayDiff > 1 && dayDiff < 7) {
    return dateFormatting.format(d, { weekday: 'long' }) ?? '';
  }
  return (
    dateFormatting.format(d, {
      month: 'long',
      day: 'numeric',
      ...(dateFormatting.calendarDay(d).year !==
      dateFormatting.calendarDay(now).year
        ? { year: 'numeric' }
        : {}),
    }) ?? ''
  );
};

/**
 * The day divider's SPOKEN form (code review, 2026-10-05): the absolute long
 * date with the year always, `October 5, 2026`. The visible label is
 * relative (`Today`, `Yesterday`, `Thursday`) and the divider has no clock to
 * refresh it, so a screen reader gets the date that cannot go stale, the way
 * the group timestamp speaks `formatWhen` under its visible clock. Empty for
 * an unparseable value, as every helper here is.
 */
export const formatDayLabelSpoken = (iso: string): string => {
  const d = dateFormatting.parseInstant(iso);
  if (!d) return '';
  return (
    dateFormatting.format(d, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }) ?? ''
  );
};

/**
 * ONE tokeniser for an author's name, shared by the avatars' initials and the
 * thread row's first-name prefix (Story 5.4 code review, C8: they name the
 * same person, so they must read the name the same way). Trimmed, split on
 * any whitespace; a blank name is no words.
 */
export const nameWords = (name?: string): string[] =>
  (name ?? '').trim().split(/\s+/).filter(Boolean);

/**
 * An avatar's initials (Story 5.4, D5; moved here for Story 5.5, C12, so the
 * thread avatar in `MessageRow` and the list avatar in the panel share it):
 * the first letter of the first and last word of the SERVER-SUPPLIED display
 * name, upper-cased. One word gives one letter; an empty or missing name
 * gives nothing, and the caller shows a glyph instead. Never derived from an
 * id (Story 2.4 AC 28 carries over).
 */
export const initials = (name?: string): string => {
  const words = nameWords(name);
  if (words.length === 0) return '';
  const letter = (w: string) => Array.from(w)[0] ?? '';
  const first = letter(words[0]);
  const last = words.length > 1 ? letter(words[words.length - 1]) : '';
  return `${first}${last}`.toUpperCase();
};

/**
 * How many files the latest live message on a thread row carries.
 *
 * Defensive about the field's absence rather than its type: the key is missing
 * on a row from a server older than this change, and missing must read as
 * "no attachments", never as `NaN`.
 */
export const attachmentCount = (thread: {
  lastMessageFileCount?: number;
}): number => {
  const n = thread.lastMessageFileCount;
  return typeof n === 'number' && Number.isFinite(n) && n > 0
    ? Math.floor(n)
    : 0;
};

/**
 * The thread row's preview line for a message that carries files and NO text,
 * or `''` when there is nothing to say (no files).
 *
 * ⚠️ IT EXISTS BECAUSE THE ROW USED TO SAY `No messages` AT A MESSAGE THAT
 * PLAINLY EXISTED. The preview branched on `lastMessageBody` alone, and an
 * attachment-only message has no body — so posting a file into a conversation
 * made its row read "No messages". `No messages` means something specific and
 * different (every message in the thread was withdrawn) and the two must not
 * collapse.
 *
 * ⚠️ A COUNT, NEVER A FILE NAME. File names are untrusted, user-supplied and
 * unbounded; in the row's one truncated line a long name becomes noise and
 * loses its own extension, while `1 attachment` is fourteen characters and
 * always fully visible. The name is one tap away in the message itself, at
 * full width. The caller keeps the `FirstName: ` prefix it uses for a text
 * preview, so an attachment-only row reads as the same kind of row it is.
 *
 * ⚠️ NO ICON, DELIBERATELY. These rows carry no `accessibilityLabel`, so a
 * screen reader reads their concatenated visible children: a paperclip glyph
 * would be silent to exactly the users who most need the line to say what
 * happened. The word carries it.
 *
 * ⚠️ ENGLISH-ONLY PLURALISATION. The ternary below is not a durable pattern;
 * a language with dual or paucal forms needs a plural-category API. Recorded
 * here so it is not copied as one.
 */
export const attachmentSummary = (thread: {
  lastMessageFileCount?: number;
}): string => {
  const n = attachmentCount(thread);
  if (n === 0) return '';
  return n === 1 ? '1 attachment' : `${n} attachments`;
};

/**
 * The `Intl` options for the thread-list row's DATE branch (Story 8.7, D1):
 * the locale's numeric short date with a two-digit year — `9/17/26` in
 * en-US, `17/09/26` in en-GB, `17.9.26` in de-DE. The owner's `9/17/26` is
 * his example, not a template: Outlook's received column shows the time for
 * today, the weekday inside the week, and past that the LOCALE's short date,
 * and the locale rule is what makes this Outlook rather than a hard-coded
 * `M/d/yy`. Exported so the pins assert against the object the helper
 * spreads, not a copy that could drift from it (a test-local `2-digit` beside
 * a code `numeric` is green and wrong). Frozen, so "a `const`, not a seam"
 * is true of the OBJECT and not only of the binding: the helper spreads this
 * very object on every call, so a consumer that mutated it would change the
 * app's date formatting process-wide.
 *
 * ⚠️ `2-digit` means two digits of WHATEVER CALENDAR THE LOCALE USES, and
 * the two digits name no century. On a Buddhist-calendar device 17 Sep 2026
 * renders `17/9/69`; `9/2/26` is 2026 or 1926 with nothing in the row to
 * say which. Both follow from "the device decides" and from the owner's own
 * two-digit example, and both are accepted here rather than overlooked.
 */
export const LIST_DATE_OPTIONS: Readonly<Intl.DateTimeFormatOptions> =
  Object.freeze({
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });

/**
 * The thread-list row's timestamp (Story 2.4, AC 5), collapsed the way a
 * mail client's received column collapses it: today → the time (`3:42 PM`),
 * within the last six days → the weekday (`Tue`), anything else → the
 * locale's numeric short date with a two-digit year (`9/17/26`; Story 8.7,
 * D1, replacing the spelled `Sep 2` / `Sep 2, 2025`). Six, not seven: at
 * seven the weekday would repeat today's and say nothing. The year is ALWAYS
 * in the third branch now, the current year included, and always two digits;
 * `now` is read for the day split only.
 *
 * The day divider and the bubble group above and inside the thread follow
 * Microsoft Teams' bands since 2026-10-05 (`formatDayLabel`,
 * `formatThreadWhen`); this column keeps Outlook's. Two rules in one panel
 * is the owner's ruling, not drift (`deferred-work.md`'s Story 2.4 "two
 * formats" entry records the chain: 2.4 D4, 5.5 D6, 8.7 D2, then the
 * 2026-10-05 Teams ruling that superseded D6 and D2 for the thread).
 *
 * Split on the CALENDAR day (`Math.round` absorbs a DST hour), so 23:59 and
 * 00:01 fall on different sides of midnight as a reader expects. A FUTURE
 * timestamp — clock skew between the server and the device — is never "this
 * week"; it falls to the date, which is at least not wrong. `now` is a
 * parameter so the branches are testable; callers leave it.
 */
export const formatListWhen = (iso: string, now: Date = new Date()): string => {
  const d = dateFormatting.parseInstant(iso);
  // Nothing, not "Invalid Date": the server gates every `lastMessageAt`
  // through `toISOString()` today, but this helper is exported and will
  // outlive that caller, and an unparseable value must never become 11px of
  // literal text squeezing the title (code review, 2026-09-10).
  if (!d) return '';
  const dayDiff = localDayDiff(d, now);
  if (dayDiff === 0) return clockOf(d);
  if (dayDiff > 0 && dayDiff < 7) {
    return dateFormatting.format(d, { weekday: 'short' }) ?? '';
  }
  return dateFormatting.format(d, LIST_DATE_OPTIONS) ?? '';
};

/** One run of the row's preview: plain, or the viewer's own token. */
export interface PreviewSegment {
  text: string;
  own: boolean;
}

/**
 * The row's preview cut into plain and OWN-token runs (#1599, owner quick-dev
 * 2026-10-26), from the server's `Thread.lastMessageOwnMentions`. The client
 * matches no names and reads no markup: the ranges are the whole answer.
 *
 * `body` is the wire's `lastMessageBody` and `shown` the trimmed text the row
 * prints. Ranges are CODE POINTS into `body` (`Array.from`, as the server
 * counts them), so an emoji before a token cannot shift it.
 *
 * ⚠️ FAILS TO A PLAIN PREVIEW, NEVER TO A WRONG HIGHLIGHT. Absent, empty, not
 * an array, unsorted, overlapping, zero-width, non-integer or out-of-range
 * ranges (adjacent ranges are fine),
 * or a `shown` that is not a prefix of `body`, all answer `null`, and the row
 * prints `shown` as it always has. The server trims nothing from the front, so
 * the prefix test only gives way when something other than the server wrote
 * the body; the trimmed tail clamps the last range rather than voiding it.
 */
export const previewSegments = (
  body: string | undefined,
  shown: string,
  ranges: ReadonlyArray<{ start: number; end: number }> | undefined,
): PreviewSegment[] | null => {
  if (body === undefined || !Array.isArray(ranges) || ranges.length === 0) {
    return null;
  }
  if (!body.startsWith(shown)) return null;
  const bodyLength = Array.from(body).length;
  const points = Array.from(shown);
  let cursor = 0;
  for (const r of ranges) {
    if (
      r === null ||
      typeof r !== 'object' ||
      !Number.isInteger(r.start) ||
      !Number.isInteger(r.end) ||
      r.start < cursor ||
      r.end <= r.start ||
      r.end > bodyLength
    ) {
      return null;
    }
    cursor = r.end;
  }
  const segments: PreviewSegment[] = [];
  let at = 0;
  for (const r of ranges) {
    const start = Math.min(r.start, points.length);
    const end = Math.min(r.end, points.length);
    if (start > at) {
      segments.push({ text: points.slice(at, start).join(''), own: false });
    }
    if (end > start) {
      segments.push({ text: points.slice(start, end).join(''), own: true });
    }
    at = Math.max(at, end);
  }
  if (at < points.length) {
    segments.push({ text: points.slice(at).join(''), own: false });
  }
  return segments.some(s => s.own) ? segments : null;
};

/**
 * `messageBodyText` and `messageBodyForEdit`, the v1 TEXT projection of a
 * stored body and its inverse, were RETIRED by #1530: the bubble renders the
 * stored HTML (`MessageBody.tsx`) and an edit loads it as HTML
 * (`messageHtml.ts`, `messageHtmlForEditor`). The entity set they decoded
 * is now `messageHtml.ts`'s, beside the server's `decodeTextEntities`.
 */
